import * as React from 'react';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc, { format } from 'owa-localize';
import { END_MATCH_DELIMITER, START_MATCH_DELIMITER } from 'owa-search';
import { WebSearchSuggestion } from 'owa-search-service';
import OpenInNewTabSuggestion from '../OpenInNewTabSuggestion';
import { SearchScenarioId } from '../../../store/schema/SearchScenarioId';
import getHighlightedTextForSuggestion from '../../../utils/getHighlightedTextForSuggestion';
import highlightDisplayText, { dehighlightDisplayText } from '../../../utils/highlightDisplayText';
import {
    findResultsOnBing,
    findResultsOnTheWeb,
    searchOnBing_3,
    searchOnTheWeb_3,
} from './WebSearchSuggestion.locstring.json';

const universalSuggestionStyles = require('../searchSuggestion/SearchSuggestion.scss');
const textSearchSuggestionStyles = require('../textSearchSuggestion/TextSearchSuggestion.scss');
const classNames = require('classnames/bind').bind(universalSuggestionStyles);

export interface WebSearchSuggestionProps {
    index: number;
    scenarioId: SearchScenarioId;
    suggestion: WebSearchSuggestion;
    suggestionSetTraceId: string;
    focusSearchInput: () => void;
}

const WebSearchSuggestion = (props: WebSearchSuggestionProps) => {
    const { index, scenarioId, suggestion, suggestionSetTraceId, focusSearchInput } = props;

    const getBingFormCode = (): string => {
        if (isFeatureEnabled('sea-webSearchSuggestion-webQuery')) {
            return 'OUTLS1';
        } else if (isFeatureEnabled('sea-webSearchSuggestion-bingQuery')) {
            return 'OUTLS2';
        } else if (isFeatureEnabled('sea-webSearchSuggestion-webResults')) {
            return 'OUTLS3';
        } else if (isFeatureEnabled('sea-webSearchSuggestion-bingResults')) {
            return 'OUTLS4';
        }

        return '';
    };

    const getLocalizedSuggestionText = (searchText: string, bingFormCode: string): string => {
        if (bingFormCode === 'OUTLS1') {
            return format(
                loc(searchOnTheWeb_3),
                START_MATCH_DELIMITER,
                searchText,
                END_MATCH_DELIMITER
            );
        } else if (bingFormCode === 'OUTLS2') {
            return format(
                loc(searchOnBing_3),
                START_MATCH_DELIMITER,
                searchText,
                END_MATCH_DELIMITER
            );
        } else if (bingFormCode === 'OUTLS3') {
            return loc(findResultsOnTheWeb);
        } else if (bingFormCode === 'OUTLS4') {
            return loc(findResultsOnBing);
        }

        return '';
    };

    /**
     * Get flight-specific suggestion content.
     */
    const sanitizedSearchText = suggestion.QueryText.trim();
    const bingFormCode = getBingFormCode();
    const locText = getLocalizedSuggestionText(sanitizedSearchText, bingFormCode);
    const highlightedTextRegions = highlightDisplayText(locText);
    const formattedSuggestionText = getHighlightedTextForSuggestion(highlightedTextRegions);

    const suggestionContent = (
        <div
            className={`${universalSuggestionStyles.contentContainer}, ${textSearchSuggestionStyles.container}`}
            style={{ maxWidth: '95%' }}>
            <Icon
                className={universalSuggestionStyles.icon}
                iconName={
                    bingFormCode === 'OUTLS1' || bingFormCode === 'OUTLS3'
                        ? ControlIcons.Search
                        : ControlIcons.BingLogo
                }
            />
            <div className={universalSuggestionStyles.webSearchSuggestionText}>
                {formattedSuggestionText}
            </div>
            <Icon
                className={classNames(
                    universalSuggestionStyles.icon,
                    universalSuggestionStyles.newTabIcon
                )}
                iconName={ControlIcons.OpenInNewTab}
            />
        </div>
    );

    return (
        bingFormCode !== '' && (
            <OpenInNewTabSuggestion
                index={index}
                scenarioId={scenarioId}
                suggestion={suggestion}
                suggestionSetTraceId={suggestionSetTraceId}
                urlToNavigateTo={`https://www.bing.com/search?q=${encodeURIComponent(
                    sanitizedSearchText
                )}&form=${bingFormCode}`}
                ariaLabel={dehighlightDisplayText(locText)}
                suggestionContent={suggestionContent}
                focusSearchInput={focusSearchInput}
            />
        )
    );
};

export default WebSearchSuggestion;
